import "./src/react-bricks/style.css"
import "intersection-observer"

import { deleteCookie, getCookie, setCookie } from "./src/shared/utils/cookie"

import { GatsbyBrowser } from "gatsby"
import { PageContext } from "./src/types"
import React from "react"
import WrapPage from "./src/gatsby-api/wrap-page"
import WrapRoot from "./src/gatsby-api/wrap-root"
import { addSearchParams } from "./src/util/add-search-params"
import client from "./src/shared/utils/client"
import { getAwc } from "./src/shared/utils/awin-mastertag"
import { languages } from "./src/shared/config/languages"
import { regions } from "./src/shared/config/regions"
import { waitFor } from "./src/shared/utils/wait-for"

const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({ location }) => {
    setTimeout(() => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: "gatsby-route-change" })
    }, 50)

    if (location.pathname.includes("/tr-tr") || location.pathname.includes("/cs-cz")) {
        const links = document.querySelectorAll("a")

        links.forEach(link => {
            link.href = addSearchParams(link.href)
        })
    }
}

type Params = "utm_source" | "utm_medium" | "utm_campaign" | "gclid" | "r"
type Body = Partial<Record<Params | "exponea_id" | "referrer" | "__lx" | "awc", string | null>>

const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
    const pathArray = window.location.pathname.split("/")
    const language =
        languages.find(l => l.langCode === pathArray[1].split("-")[0])?.langCode || process.env.DEFAULT_LANGUAGE
    const search = new URLSearchParams(window.location.search)
    const filteredRegions = regions.filter(r => !process.env.NO_SITE_REGIONS.includes(r.countryCode))
    const region =
        filteredRegions.find(r => r.countryCode === pathArray[1].split("-")?.[1]?.toUpperCase())?.countryCode ||
        filteredRegions.find(r => r.countryCode === search.get("region"))?.countryCode ||
        getCookie("__region__") ||
        process.env.REGION
    setCookie("__language__", language, { sameSite: "Lax" })
    setCookie("__region__", region, { sameSite: "Lax" })

    if (["TR", "CZ"].includes(region)) {
        const getParamsFromUrl = (params: Params[]) => {
            const urlParams = new URLSearchParams(window.location.search)
            const result: Partial<Record<Params, string>> = {}
            params.forEach(param => {
                const value = urlParams.get(param)
                if (value) {
                    result[param] = value
                }
            })
            return result
        }

        const storeParamsInCookies = (params: Params[]) => {
            const paramsFromUrl = getParamsFromUrl(params)
            Object.keys(paramsFromUrl).forEach(param => {
                const cookieName = `__${param}__`
                const value = paramsFromUrl[param as Params]
                if (!getCookie(cookieName) && value) {
                    setCookie(cookieName, value)
                }
            })
        }

        const paramsToStore: Params[] = ["utm_source", "utm_medium", "utm_campaign", "gclid", "r"]
        storeParamsInCookies(paramsToStore)
    }

    const track = () => {
        const params = new URLSearchParams(window.location.search)

        const getParamOrCookie = (param: Params) => {
            return params.get(param) || getCookie(`__${param}__`)
        }

        let body: Body = {
            exponea_id: getCookie("__exponea_etc__"),
        }
        if (document.referrer && !document.referrer.includes(process.env.HOSTNAME)) {
            body.referrer = document.referrer
        }
        if (getParamOrCookie("utm_source")) {
            body.utm_source = getParamOrCookie("utm_source")
        }
        if (getParamOrCookie("utm_medium")) {
            body.utm_medium = getParamOrCookie("utm_medium")
        }
        if (getParamOrCookie("utm_campaign")) {
            body.utm_campaign = getParamOrCookie("utm_campaign")
        }
        if (params.get("gclid")) {
            body.gclid = params.get("gclid")
        }
        if (params.get("__lx")) {
            body.__lx = params.get("__lx")
        }
        if (getAwc()) {
            body.awc = getAwc()
        }
        if (Object.keys(body).length > 1 && body.exponea_id && !["TR", "CZ"].includes(region)) {
            try {
                client.createCustomerTracking(body)

                deleteCookie("__utm_source__")
                deleteCookie("__utm_medium__")
                deleteCookie("__utm_campaign__")
            } catch (error) {
                return
            }
        }
    }

    window.addEventListener(
        "exponea:load",
        () => waitFor(() => !!(getCookie("__exponea_etc__") && client.state.consumerToken), track),
        { once: true }
    )
}

const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({ element }) => {
    return <WrapRoot>{element}</WrapRoot>
}
const wrapPageElement: GatsbyBrowser<{}, PageContext>["wrapPageElement"] = ({ element, props }) => {
    return <WrapPage props={props}>{element}</WrapPage>
}

export { onRouteUpdate, wrapRootElement, wrapPageElement, onClientEntry }
